import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "./ui/sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { feeStatusLabel, feeStatusColor, formatMoney } from "../lib/feeStatus";

const EMPTY_REPORT = { payment_method: "venmo", amount_sent: "", sender_username: "", transaction_reference: "", date_paid: "" };

/** Shared Venmo/CashApp booking-fee panel used on both the Stranger's BookingDetail
 * and the Photographer's SessionManage. Strictly manual: no automated processors. */
export const FeePaymentPanel = ({ bookingId, partyType, bookingNumber, wrapperClassName = "" }) => {
  const [fee, setFee] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [report, setReport] = useState(EMPTY_REPORT);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api
      .get(`/bookings/${bookingId}/fees/${partyType}`)
      .then(({ data }) => setFee(data))
      .catch(() => setFee(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, partyType]);

  const submitReport = async () => {
    setSubmitting(true);
    try {
      await api.post(`/bookings/${bookingId}/fees/${partyType}/report-payment`, {
        payment_method: report.payment_method,
        amount_sent: Number(report.amount_sent),
        sender_username: report.sender_username || null,
        transaction_reference: report.transaction_reference || null,
        date_paid: report.date_paid || null,
      });
      toast.success("Payment reported — we'll verify it shortly.");
      setReporting(false);
      setReport(EMPTY_REPORT);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (fee === null) return null;
  if (fee === false || fee.status === "not_required") return null;

  const canReport = ["awaiting_payment", "payment_not_found", "incorrect_amount", "partially_paid"].includes(fee.status);
  const isFinal = ["verified", "waived", "refunded", "cancelled"].includes(fee.status);

  return (
    <div className={`border border-soft-gray rounded-2xl p-6 ${wrapperClassName}`} data-testid={`fee-panel-${partyType}`}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-extrabold uppercase text-sm">Stranger Sessions Booking Fee</h3>
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full whitespace-nowrap ${feeStatusColor(fee.status)}`} data-testid="fee-panel-status">
          {feeStatusLabel(fee.status)}
        </span>
      </div>
      <p className="text-sm font-medium text-midnight-black/70 mb-4">
        Amount due: <strong>{formatMoney(fee.remaining_amount ?? fee.final_amount)}</strong>
        {fee.discount_amount > 0 && <span className="text-midnight-black/50"> ({formatMoney(fee.original_amount)} minus {formatMoney(fee.discount_amount)} discount)</span>}
      </p>

      {isFinal && (
        <p className="text-sm font-medium text-midnight-black/60" data-testid="fee-panel-final-message">
          {fee.status === "verified" ? "Payment verified — you're all set." : fee.status === "waived" ? "This fee has been waived." : fee.status === "refunded" ? "This fee was refunded." : "No further action needed."}
        </p>
      )}

      {canReport && !reporting && (
        <>
          <div className="bg-warm-white rounded-xl p-4 mb-4 text-sm font-medium text-midnight-black/80" data-testid="fee-payment-instructions">
            <p className="mb-1">Send <strong>{formatMoney(fee.remaining_amount ?? fee.final_amount)}</strong> via:</p>
            <p>Venmo: <strong>@{fee.venmo_username}</strong> &nbsp;|&nbsp; Cash App: <strong>${fee.cashapp_username}</strong></p>
            <p className="mt-2 text-xs text-midnight-black/60">Include your booking number <strong>{bookingNumber}</strong> in the payment note so we can match it.</p>
          </div>
          <Button data-testid="report-payment-button" onClick={() => setReporting(true)} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full">
            I'VE PAID
          </Button>
        </>
      )}

      {reporting && (
        <div className="space-y-3" data-testid="report-payment-form">
          <Select value={report.payment_method} onValueChange={(v) => setReport({ ...report, payment_method: v })}>
            <SelectTrigger data-testid="report-payment-method-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="venmo">Venmo</SelectItem>
              <SelectItem value="cashapp">Cash App</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="report-amount-input" type="number" placeholder="Amount sent" value={report.amount_sent} onChange={(e) => setReport({ ...report, amount_sent: e.target.value })} />
          <Input data-testid="report-sender-username-input" placeholder="Your Venmo/Cash App username" value={report.sender_username} onChange={(e) => setReport({ ...report, sender_username: e.target.value })} />
          <Input data-testid="report-transaction-ref-input" placeholder="Transaction reference (optional)" value={report.transaction_reference} onChange={(e) => setReport({ ...report, transaction_reference: e.target.value })} />
          <Input data-testid="report-date-paid-input" type="date" value={report.date_paid} onChange={(e) => setReport({ ...report, date_paid: e.target.value })} />
          <div className="flex gap-3">
            <Button data-testid="submit-payment-report-button" disabled={submitting || !report.amount_sent} onClick={submitReport} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full">
              {submitting ? "Submitting..." : "SUBMIT"}
            </Button>
            <Button variant="outline" data-testid="cancel-payment-report-button" onClick={() => setReporting(false)} className="rounded-full font-bold">
              CANCEL
            </Button>
          </div>
        </div>
      )}

      {fee.status === "verification_pending" && (
        <p className="text-sm font-medium text-stranger-orange" data-testid="fee-panel-pending-message">
          We're reviewing your payment. We'll confirm your booking as soon as it's verified.
        </p>
      )}
    </div>
  );
};
